import {
  DEFAULT_FONT_SIZE,
  resolveFontSize,
  themeNames,
  type ThemeName
} from 'pixelize-design-library';

import { DEFAULT_LANGUAGE, isSupportedLanguage, type LanguageCode } from '@core/i18n/languages';

export type AppColorMode = 'light' | 'dark' | 'system';

export interface AppearanceSettings {
  theme: ThemeName;
  colorMode: AppColorMode;
  language: LanguageCode;
  fontSize: string;
}

const SETTINGS_KEY = 'lfour-settings';
/** Written by the pre-Redux zustand language store; read so a saved language survives. */
const LEGACY_LANGUAGE_KEY = 'lfour-lang';

export const DEFAULT_BRAND: ThemeName = 'lavender';

/**
 * `light`, not `system`: a first-time visitor on a dark-preferring OS would otherwise land on
 * the login page in dark mode without ever having asked for it. Dark mode stays fully
 * supported — it is opt-in through the theme switcher, and the choice then persists.
 */
export const DEFAULT_SETTINGS: AppearanceSettings = {
  theme: DEFAULT_BRAND,
  colorMode: 'light',
  language: DEFAULT_LANGUAGE,
  fontSize: DEFAULT_FONT_SIZE
};

const isThemeName = (value: unknown): value is ThemeName =>
  typeof value === 'string' && themeNames.includes(value as ThemeName);

const isColorMode = (value: unknown): value is AppColorMode =>
  value === 'light' || value === 'dark' || value === 'system';

function readLegacyLanguage(): LanguageCode | null {
  const raw = window.localStorage.getItem(LEGACY_LANGUAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { state?: { lang?: unknown } };
    const lang = parsed.state?.lang;
    return isSupportedLanguage(lang) ? lang : null;
  } catch {
    return null;
  }
}

/**
 * Appearance is seeded from storage rather than the server because server prefs are
 * token-gated: `/login` renders before any request can succeed, and a light flash
 * followed by a dark repaint is the whole bug this avoids.
 */
export function readStoredSettings(): AppearanceSettings {
  const legacyLanguage = readLegacyLanguage();
  const raw = window.localStorage.getItem(SETTINGS_KEY);
  if (!raw) {
    return { ...DEFAULT_SETTINGS, language: legacyLanguage ?? DEFAULT_SETTINGS.language };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof AppearanceSettings, unknown>>;
    return {
      theme: isThemeName(parsed.theme) ? parsed.theme : DEFAULT_SETTINGS.theme,
      colorMode: isColorMode(parsed.colorMode) ? parsed.colorMode : DEFAULT_SETTINGS.colorMode,
      language: isSupportedLanguage(parsed.language)
        ? parsed.language
        : (legacyLanguage ?? DEFAULT_SETTINGS.language),
      fontSize: resolveFontSize(typeof parsed.fontSize === 'string' ? parsed.fontSize : null)
    };
  } catch {
    window.localStorage.removeItem(SETTINGS_KEY);
    return { ...DEFAULT_SETTINGS, language: legacyLanguage ?? DEFAULT_SETTINGS.language };
  }
}

export function writeStoredSettings(settings: AppearanceSettings): void {
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}
